import * as React from 'react';
import {YearMonth, YearMonthRangePicker} from "./utils/YearMonthRangePicker";
import SectionHeaderFormItem from "../common/form-items/SectionHeaderFormItem";
import locales from "../locales";
import {CategoryValue} from "../api/dtos/statistics";
import {
  renderLegendDot,
  renderPercentTextIfPossible,
  renderPriceStatLayoutItem,
  renderStatLayoutItem,
  renderTitledStatLayout
} from "./utils/statistics-utils";
import {ChartItem, RentimeChart} from "./RentimeChart";
import {groupNumber, withCurrency} from "../utils/number-utils";
import {getColor} from "./utils/statistics-colors";
import {Alert, Select} from "antd";
import StaticFormItem from "../common/form-items/StaticFormItem";

interface Props {
  onQueryParamsChange: (start: YearMonth, end: YearMonth, responsibleUser?: string) => void;
  costStatistics?: CategoryValue[];
  usageStatistics?: CategoryValue[];
  users?: string[];
}

interface State {
  start?: YearMonth;
  end?: YearMonth;
  responsibleUser?: string;
}

export default class CategoryDistributionStatisticsPanel extends React.Component<Props, State> {
  public state: State = {
    responsibleUser: undefined,
  };

  public render(): React.ReactNode {
    const {costStatistics, usageStatistics, users} = this.props;
    const {responsibleUser} = this.state;

    const noStatsAvailable = costStatistics && costStatistics.length === 0 && usageStatistics && usageStatistics.length === 0;

    return (
      <>
        {users &&
            <>
                <StaticFormItem label={locales.responsible_user} initialValue={
                  <Select
                    onChange={this.onUserPick}
                    value={responsibleUser}
                    placeholder={locales.all}
                    allowClear={true}
                    disabled={users.length < 1}
                    style={{width: 180}}
                  >
                    {this.props.users!.map(value => (
                      <Select.Option key={value} value={value}>
                        {value}
                      </Select.Option>
                    ))}
                  </Select>
                }/>
            </>
        }
        <SectionHeaderFormItem title={locales.range}/>
        <YearMonthRangePicker
          onRangeClear={this.onRangeClear}
          onRangePick={this.onRangePick}
        />

        {noStatsAvailable && this.renderNoStatisticsAvailable()}
        {!noStatsAvailable && costStatistics && this.renderCostStatistics(costStatistics)}
        {!noStatsAvailable && usageStatistics && this.renderUsageStatistics(usageStatistics)}
      </>
    );
  }

  private onRangeClear = () => {
    this.setState({
      start: undefined,
      end: undefined,
    });
  };

  private onRangePick = (start: YearMonth | undefined, end: YearMonth | undefined) => {
    this.setState({start, end});
    if (start && end) {
      this.props.onQueryParamsChange(start, end, this.state.responsibleUser)
    }
  };

  private onUserPick = (user?: string) => {
    this.setState({responsibleUser: user})
    const {start, end} = this.state;
    if (start && end) {
      this.props.onQueryParamsChange(start, end, user)
    }
  };


  private renderNoStatisticsAvailable = (): React.ReactNode => {
    return renderTitledStatLayout('', renderStatLayoutItem('', <Alert type="info"
                                                                      message={locales.statistics_no_data}/>));
  };

  private renderCostStatistics = (incomeStats: CategoryValue[]): React.ReactNode => {
    const chartData = this.mapStatsToChartData(incomeStats);
    const totalIncome = incomeStats.map(s => s.value).reduce((a, b) => a + b, 0);

    return renderTitledStatLayout(
      locales.income,
      <>
        <b>{renderPriceStatLayoutItem(locales.total_income, totalIncome)}</b>
        {incomeStats.map((stat, index) => this.renderCategoryPriceStat(stat, totalIncome, index))}
      </>,
      <>
        {totalIncome > 0 && <RentimeChart unit={locales.app_currency} data={chartData}/>}
      </>
    );
  };

  private renderCategoryPriceStat = (stat: CategoryValue, total: number, index: number) => {
    return renderStatLayoutItem(
      stat.category,
      <>
        {renderLegendDot(getColor(index))}
        {withCurrency(groupNumber(stat.value))}
        {renderPercentTextIfPossible(stat.value, total)}
      </>
    );
  };

  private renderUsageStatistics = (usageStats: CategoryValue[]): React.ReactNode => {
    const chartData = this.mapStatsToChartData(usageStats);
    const totalDays = usageStats.map(s => s.value).reduce((a, b) => a + b, 0);
    return renderTitledStatLayout(
      locales.usage,
      <>
        {usageStats.map((stat, index) => this.renderUsageStat(stat, totalDays, index))}
        {this.renderInfoAlert()}
      </>,
      <RentimeChart
        unit={locales.day}
        data={chartData}
      />
    );
  };

  private renderInfoAlert = () => {
    return renderStatLayoutItem('', <Alert type="info" message={locales.usage_statistics_notes}/>);
  };

  private renderUsageStat = (stat: CategoryValue, total: number, index: number) => {
    return renderStatLayoutItem(
      stat.category,
      <>
        {renderLegendDot(getColor(index))}
        {<>{groupNumber(stat.value)} {locales.day}</>}
        {renderPercentTextIfPossible(stat.value, total)}
      </>
    );
  };

  private mapStatsToChartData = (stats: CategoryValue[]): ChartItem[] => {
    return stats.map(stat => {
      return {
        name: stat.category,
        value: stat.value
      };
    });
  };
}


// WEBPACK FOOTER //
// ./src/statistics/CategoryDistributionStatisticsPanel.tsx